export const FEATURE_GOALS = [
  {
    id: 'mobile-apps',
    name: 'Build a mobile app',
    description: 'Create an application for phones and tablets.',
    featureIds: [
      'ui-component-library',
      'responsive-design',
      'ios-ipados',
      'android',
      'granular-schema-field-types',
      'data-model-relationships',
      'device-enrollment',
      'application-update-delivery'
    ]
  },
  {
    id: 'business-operations',
    name: 'Digitize a manual process in my organization',
    description: 'Replace manual steps with connected forms, data, and workflows.',
    featureIds: [
      'ui-component-library',
      'object-table',
      'granular-schema-field-types',
      'data-model-relationships',
      'app-roles-and-data-access',
      'browse-and-edit-records',
      'testing-staging-and-production-deployments'
    ]
  },
  {
    id: 'integrations',
    name: 'Connect external systems together',
    description: 'Exchange data and coordinate work between existing systems.',
    featureIds: [
      'project-builds',
      'backend-api',
      'cloudcode-http',
      'external-npm-packages',
      'database-access',
      'cloudcode-queues',
      'log-search'
    ]
  },
  {
    id: 'reporting',
    name: 'Build a report on top of existing data',
    description: 'Read existing data and generate a report.',
    featureIds: [
      'project-builds',
      'database-access',
      'external-npm-packages',
      'pdf-reports-in-cloudcode',
      'file-and-attachment-storage'
    ]
  },
  {
    id: 'automation',
    name: 'Run a task periodically in the cloud',
    description: 'Run scheduled work without an app interface.',
    featureIds: [
      'project-builds',
      'scheduled-execution',
      'external-npm-packages',
      'database-access',
      'cloudcode-queues',
      'log-search'
    ]
  }
] as const;

export type FeatureGoalId = (typeof FEATURE_GOALS)[number]['id'];

interface FeatureWizardOption {
  id: string;
  name: string;
  description: string;
  goals: readonly FeatureGoalId[];
  featureIds: readonly string[];
  featuresByGoal?: Partial<Record<FeatureGoalId, readonly string[]>>;
  featuresByTrait?: Partial<Record<FeatureTraitId, readonly string[]>>;
}

export const FEATURE_TRAITS = [
  {
    id: 'custom-branding',
    name: 'Use custom branding',
    description: 'Style the app and distribute containers with your own branding.',
    goals: ['mobile-apps', 'business-operations', 'reporting'],
    featureIds: ['themes-and-app-styling', 'custom-branded-builds']
  },
  {
    id: 'web-browser',
    name: 'Access in a web browser',
    description: 'Use the Web Container to open the app in a supported desktop browser.',
    goals: ['mobile-apps', 'business-operations', 'reporting'],
    featureIds: ['web-browser-access', 'web-multiple-tabs', 'ui-component-library']
  },
  {
    id: 'desktop',
    name: 'Run as a desktop app',
    description: 'Run in a native desktop container on Windows, macOS, or Linux.',
    goals: ['mobile-apps', 'business-operations', 'reporting'],
    featureIds: ['desktop-support', 'ui-component-library']
  },
  {
    id: 'push',
    name: 'Send push notifications',
    description: 'Notify app users with push notifications on supported platforms.',
    goals: ['mobile-apps', 'business-operations'],
    featureIds: ['push-notifications']
  },
  {
    id: 'location',
    name: 'Capture GPS locations',
    description: 'Capture coordinates and record location tracks from supported devices.',
    goals: ['mobile-apps', 'business-operations'],
    featureIds: ['gps-capture-and-tracking']
  },

  {
    id: 'browse-reports',
    name: 'Browse reports',
    description: 'Give users an interface for finding and viewing reports.',
    goals: ['mobile-apps', 'business-operations', 'reporting'],
    featureIds: ['object-table', 'file-capture-and-display', 'ui-component-library']
  },
  {
    id: 'send-email',
    name: 'Send an email',
    description: 'Send messages through an email provider from CloudCode.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['project-builds', 'email-delivery', 'external-npm-packages'],
    featuresByGoal: { 'mobile-apps': ['trigger-from-the-runtime'], 'business-operations': ['trigger-from-the-runtime'] }
  },
  {
    id: 'sso',
    name: 'Enroll with SSO',
    description: 'Let app users sign in and enroll through an identity provider.',
    goals: ['mobile-apps', 'business-operations'],
    featureIds: [
      'external-sso',
      'device-enrollment',
      'app-and-device-sessions',
      'authentication-scope-management',
      'app-sso-configuration'
    ]
  },
  {
    id: 'offline',
    name: 'Work offline',
    description: 'Keep app workflows available without a network connection.',
    goals: ['mobile-apps', 'business-operations'],
    featureIds: ['offline-first-data-sync', 'data-rules-and-acls']
  },
  {
    id: 'capture-files',
    name: 'Capture files and signatures',
    description: 'Collect documents and signed evidence in app workflows.',
    goals: ['mobile-apps', 'business-operations'],
    featureIds: ['file-capture-and-display', 'signature-capture-and-display', 'file-and-attachment-storage']
  },
  {
    id: 'external-data',
    name: 'Read or update external data',
    description: 'Exchange records with external systems using APIs and integrations.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['project-builds', 'backend-api', 'external-npm-packages', 'cloudcode-http']
  },
  {
    id: 'pdf',
    name: 'Generate a PDF',
    description: 'Create documents and reports in CloudCode.',
    goals: ['mobile-apps', 'business-operations', 'reporting', 'automation'],
    featureIds: ['project-builds', 'pdf-reports-in-cloudcode', 'file-and-attachment-storage']
  },
  {
    id: 'data-events',
    name: 'React to data changes',
    description: 'Start work when records are created, updated, or deleted.',
    goals: ['business-operations', 'integrations', 'automation'],
    featureIds: ['project-builds', 'data-change-triggers', 'external-webhooks']
  },
  {
    id: 'dashboards',
    name: 'Embed dashboards',
    description: 'Present Power BI dashboards inside an app.',
    goals: ['mobile-apps', 'business-operations', 'reporting'],
    featureIds: ['embedded-power-bi-dashboards', 'ui-component-library']
  },
  {
    id: 'hardware',
    name: 'Communicate with hardware devices',
    description: 'Use native APIs for supported industrial devices and peripherals.',
    goals: ['mobile-apps', 'business-operations'],
    featureIds: ['bluetooth-low-energy-ble', 'serial-port-access', 'tcp-sockets', 'hardware-barcode-scanning']
  }
] as const satisfies readonly FeatureWizardOption[];
export type FeatureTraitId = (typeof FEATURE_TRAITS)[number]['id'];

export const FEATURE_BUILD_METHODS = [
  {
    id: 'visual',
    name: 'Drag and drop',
    description: 'Use visual editors for views and schemas, alongside code when needed.',
    goals: ['mobile-apps', 'business-operations', 'reporting'],
    featureIds: ['visual-editing', 'drag-and-drop']
  },
  {
    id: 'code',
    name: 'Web IDE',
    description: 'Write and edit application logic and cloud tasks in OXIDE.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['smart-autocomplete-and-intellisense', 'code-analysis'],
    featuresByGoal: {
      'mobile-apps': ['xml', 'global-runtime-apis'],
      'business-operations': ['xml', 'global-runtime-apis']
    }
  },
  {
    id: 'offline-editor',
    name: 'My own offline editor',
    description: 'Edit project source locally using your own editor and an external Git repository.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['external-git-repositories', 'source-drafts-and-history']
  },
  {
    id: 'ai',
    name: 'AI tools',
    description: 'Use AI development tools with platform context and CLI access.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['ai-development', 'cli-source-and-operations']
  },
  {
    id: 'github',
    name: 'My own Git repo',
    description: 'Connect a repository hosted on GitHub or Azure DevOps.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['external-git-repositories', 'source-drafts-and-history']
  },
  {
    id: 'advanced-git',
    name: 'Advanced Git support',
    description: 'Use separate drafts, branches, and conflict resolution for source control.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['full-git-support-with-conflict-resolution', 'source-drafts-and-history']
  }
] as const satisfies readonly FeatureWizardOption[];
export type FeatureBuildMethodId = (typeof FEATURE_BUILD_METHODS)[number]['id'];

export const FEATURE_LANGUAGES = [
  {
    id: 'javascript',
    name: 'JavaScript',
    description: 'Build with JavaScript.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: [],
    featuresByGoal: {
      'mobile-apps': ['javascript'],
      'business-operations': ['javascript'],
      integrations: ['cloudcode-javascript'],
      reporting: ['cloudcode-javascript'],
      automation: ['cloudcode-javascript']
    },
    featuresByTrait: {
      'custom-branding': ['javascript'],
      'web-browser': ['javascript'],
      desktop: ['javascript'],
      'send-email': ['cloudcode-javascript'],
      'external-data': ['cloudcode-javascript'],
      pdf: ['cloudcode-javascript'],
      'data-events': ['cloudcode-javascript']
    }
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    description: 'Build with TypeScript.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['project-builds'],
    featuresByGoal: {
      'mobile-apps': ['typescript'],
      'business-operations': ['typescript'],
      integrations: ['cloudcode-typescript'],
      reporting: ['cloudcode-typescript'],
      automation: ['cloudcode-typescript']
    },
    featuresByTrait: {
      'custom-branding': ['typescript'],
      'web-browser': ['typescript'],
      desktop: ['typescript'],
      'send-email': ['cloudcode-typescript'],
      'external-data': ['cloudcode-typescript'],
      pdf: ['cloudcode-typescript'],
      'data-events': ['cloudcode-typescript']
    }
  }
] as const satisfies readonly FeatureWizardOption[];
export type FeatureLanguageId = (typeof FEATURE_LANGUAGES)[number]['id'];

export const FEATURE_TEAM_OPTIONS = [
  {
    id: 'solo',
    name: 'On my own',
    description: 'Develop the project independently.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: []
  },
  {
    id: 'team',
    name: 'In a team',
    description: 'Manage team members, roles, and custom policies.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['team-administration', 'team-access', 'custom-roles', 'custom-policies', 'policy-editor']
  }
] as const satisfies readonly FeatureWizardOption[];
export type FeatureTeamId = (typeof FEATURE_TEAM_OPTIONS)[number]['id'];

export const FEATURE_GOVERNANCE_OPTIONS = [
  {
    id: 'enterprise-auditing',
    name: 'Enterprise auditing',
    description: 'Review audit information in OVERRIDE, backend audit logs, and recorded data changes.',
    goals: ['mobile-apps', 'business-operations', 'integrations', 'reporting', 'automation'],
    featureIds: ['audit-review', 'audit-logs', 'change-logs']
  }
] as const satisfies readonly FeatureWizardOption[];
export type FeatureGovernanceId = (typeof FEATURE_GOVERNANCE_OPTIONS)[number]['id'];

export const getAvailableTraits = (goals: readonly FeatureGoalId[]) =>
  FEATURE_TRAITS.filter((option) => goals.some((goal) => (option.goals as readonly FeatureGoalId[]).includes(goal)));
export const getAvailableBuildMethods = (goals: readonly FeatureGoalId[]) =>
  FEATURE_BUILD_METHODS.filter((option) =>
    goals.some((goal) => (option.goals as readonly FeatureGoalId[]).includes(goal))
  );

/**
 * Assemble the explicit feature mappings for the selected goals and options.
 * System connections do not add recommendations; changes belong in the matrix above.
 */
export const getRecommendedFeatureIds = (
  goals: readonly FeatureGoalId[],
  traits: readonly FeatureTraitId[],
  methods: readonly FeatureBuildMethodId[],
  language?: FeatureLanguageId,
  team?: FeatureTeamId,
  governance: readonly FeatureGovernanceId[] = []
): Set<string> => {
  const ids = new Set<string>(
    FEATURE_GOALS.filter((goal) => goals.includes(goal.id)).flatMap((goal) => [...goal.featureIds])
  );
  const options: readonly FeatureWizardOption[] = [
    ...getAvailableTraits(goals).filter((option) => traits.includes(option.id)),
    ...getAvailableBuildMethods(goals).filter((option) => methods.includes(option.id)),
    ...FEATURE_LANGUAGES.filter((option) => option.id === language),
    ...FEATURE_TEAM_OPTIONS.filter((option) => option.id === team),
    ...FEATURE_GOVERNANCE_OPTIONS.filter((option) => governance.includes(option.id))
  ];
  for (const option of options) {
    option.featureIds.forEach((id) => ids.add(id));
    goals.forEach((goal) => option.featuresByGoal?.[goal]?.forEach((id) => ids.add(id)));
    traits.forEach((trait) => option.featuresByTrait?.[trait]?.forEach((id) => ids.add(id)));
  }
  return ids;
};
